import React, { useState, useEffect, useRef } from 'react';

/**
 * BaseModal Component - Best Practice Modal Implementation
 * @param {Object} props
 * @param {boolean} props.show - Modal gösterim durumu
 * @param {function} props.onHide - Modal kapatma fonksiyonu
 * @param {string} props.title - Modal başlığı
 * @param {React.ReactNode} props.children - Modal içeriği
 * @param {string} props.size - Modal boyutu ('sm', 'md', 'lg', 'xl', veya undefined)
 * @param {boolean} props.fullScreenOnMobile - Mobilde tam ekran olsun mu? (default: true)
 * @param {React.ReactNode} props.footer - Modal footer içeriği (opsiyonel)
 * @param {string} props.className - Ek CSS sınıfları (opsiyonel)
 * @param {boolean} props.closeOnBackdrop - Backdrop'a tıklayınca kapansın mı? (default: true)
 * @param {boolean} props.closeOnEscape - ESC tuşuna basınca kapansın mı? (default: true)
 * @param {string} props.ariaLabel - ARIA label for accessibility
 */
function BaseModal({ 
  show, 
  onHide, 
  title, 
  children, 
  size,
  fullScreenOnMobile = true,
  footer,
  className = '',
  closeOnBackdrop = true,
  closeOnEscape = true,
  ariaLabel
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard navigation (ESC key)
  useEffect(() => {
    if (!show || !closeOnEscape) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onHide();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [show, closeOnEscape, onHide]);

  // Focus management and body scroll lock
  useEffect(() => {
    if (show) {
      // Store previous active element
      previousActiveElement.current = document.activeElement;
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      // Focus first focusable element after animation
      setTimeout(() => {
        const firstFocusable = modalRef.current?.querySelector(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 150);
    } else {
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      
      // Restore focus to previous element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [show]);

  // Focus trap - keep focus within modal
  useEffect(() => {
    if (!show) return;

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [show]);

  if (!show) return null;

  // Modal size class
  const getSizeClass = () => {
    if (isMobile && fullScreenOnMobile) return 'modal-fullscreen-md-down';
    if (!size) return 'modal-lg';
    return `modal-${size}`;
  };

  const handleBackdropClick = (e) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onHide();
    }
  };

  return (
    <div 
      className={`modal fade ${show ? 'show' : ''}`} 
      style={{ display: show ? 'block' : 'none' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
      aria-label={ariaLabel || title}
      onClick={handleBackdropClick}
    >
      <div 
        className={`modal-dialog ${getSizeClass()} modal-dialog-centered ${className}`}
        ref={contentRef}
      >
        <div 
          className="modal-content" 
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-scroll-wrapper">
            {/* Header */}
            <div className="modal-header">
              {title && (
                <h5 className="modal-title" id="modal-title">{title}</h5>
              )}
              <button 
                type="button" 
                className="btn-close" 
                onClick={onHide}
                aria-label="Kapat"
                tabIndex={0}
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="modal-footer">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Backdrop */}
      <div 
        className={`modal-backdrop fade ${show ? 'show' : ''}`}
        onClick={closeOnBackdrop ? onHide : undefined}
        aria-hidden="true"
      ></div>

      <style>
        {`
          .modal {
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1050;
            animation: fadeIn 0.2s ease-out;
          }

          .modal.fade {
            opacity: 0;
            transition: opacity 0.15s linear;
          }

          .modal.fade.show {
            opacity: 1;
          }

          .modal-dialog {
            z-index: 1055;
            transform: scale(0.9);
            transition: transform 0.2s ease-out;
          }

          .modal.fade.show .modal-dialog {
            transform: scale(1);
          }
          
          .modal-backdrop {
            z-index: 1040;
            background-color: rgba(0, 0, 0, 0.5);
            transition: opacity 0.15s linear;
          }

          .modal-backdrop.fade {
            opacity: 0;
          }

          .modal-backdrop.fade.show {
            opacity: 1;
          }

          .modal-content {
            border: none;
            border-radius: ${isMobile ? '0' : '12px'};
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
            max-height: ${isMobile ? '100vh' : 'calc(100vh - 3.5rem)'};
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .modal-scroll-wrapper {
            display: flex;
            flex-direction: column;
            min-height: min-content;
            max-height: ${isMobile ? '100vh' : 'calc(100vh - 3.5rem)'};
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          .modal-header {
            padding: ${isMobile ? '1rem' : '1.25rem 1.5rem'};
            border-bottom: 1px solid #e9ecef;
            background: white;
            position: sticky;
            top: 0;
            z-index: 1;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .modal-title {
            font-size: ${isMobile ? '1.1rem' : '1.25rem'};
            font-weight: 600;
            color: #212529;
            margin: 0;
            line-height: 1.5;
          }

          .modal-body {
            padding: ${isMobile ? '1rem' : '1.5rem'};
            flex: 1;
            overflow-y: auto;
          }

          .modal-footer {
            padding: ${isMobile ? '1rem' : '1rem 1.5rem'};
            border-top: 1px solid #e9ecef;
            background: white;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.5rem;
          }

          .btn-close {
            padding: 0.5rem;
            margin: -0.5rem -0.5rem -0.5rem auto;
            opacity: 0.5;
            transition: opacity 0.15s ease-in-out;
            border-radius: 0.25rem;
          }

          .btn-close:hover {
            opacity: 1;
            background-color: rgba(0, 0, 0, 0.05);
          }

          .btn-close:focus {
            box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.1);
            outline: none;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @media (max-width: 767px) {
            .modal-fullscreen-md-down {
              max-width: 100%;
              margin: 0;
              height: 100%;
            }
            
            .modal-fullscreen-md-down .modal-content {
              height: 100%;
              border: 0;
              border-radius: 0 !important;
            }

            .modal-fullscreen-md-down .modal-dialog {
              transform: translateY(0);
            }
          }

          /* Smooth scrollbar */
          .modal-scroll-wrapper::-webkit-scrollbar {
            width: 8px;
          }

          .modal-scroll-wrapper::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          .modal-scroll-wrapper::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }

          .modal-scroll-wrapper::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}
      </style>
    </div>
  );
}

export default BaseModal; 